import { LockOutlined, UserOutlined } from "@ant-design/icons";
import { signIn } from "aws-amplify/auth";
import type { FormEvent } from "react";
import { useState } from "react";

interface ApiLoginProps {
    onLogin: () => void | Promise<void>;
}

const ApiLogin = ({ onLogin }: ApiLoginProps) => {
    const [credentials, setCredentials] = useState({ username: "", password: "" });
    const [error, setError] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const submit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!credentials.username.trim() || !credentials.password) return;

        setIsLoading(true);
        setError("");
        try {
            const result = await signIn({
                username: credentials.username.trim(),
                password: credentials.password,
                options: { authFlowType: 'USER_PASSWORD_AUTH' }
            });
            if (!result.isSignedIn) {
                throw new Error(`Additional sign-in step required: ${result.nextStep.signInStep}`);
            }
            setCredentials(current => ({ ...current, password: "" }));
            await onLogin();
        } catch (loginError) {
            setError(loginError instanceof Error ? loginError.message : "Login failed. Please check your credentials.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <form className="mx-auto flex min-h-80 w-full max-w-sm flex-col justify-center" onSubmit={submit}>
            <div className="mb-6 text-center">
                <h3 className="m-0 text-base font-bold text-slate-100">Sign in to access APIs</h3>
                <p className="mb-0 mt-1 text-xs text-slate-500">Use your Ocean Planner account.</p>
            </div>
            <label className="mb-4 block !pl-0">
                <span className="mb-1.5 block text-xs font-semibold text-slate-300">Username</span>
                <div className="flex h-10 items-center gap-2 rounded-md border border-slate-700 bg-slate-950 px-3 focus-within:border-indigo-500">
                    <UserOutlined className="text-slate-500" />
                    <input
                        autoComplete="username"
                        autoFocus
                        className="min-w-0 flex-1 bg-transparent text-sm text-slate-200 outline-none [&:-webkit-autofill]:[-webkit-box-shadow:0_0_0_1000px_#020617_inset] [&:-webkit-autofill]:[-webkit-text-fill-color:#e2e8f0]"
                        onChange={event => setCredentials(current => ({ ...current, username: event.target.value }))}
                        required
                        value={credentials.username}
                    />
                </div>
            </label>
            <label className="mb-5 block !pl-0">
                <span className="mb-1.5 block text-xs font-semibold text-slate-300">Password</span>
                <div className="flex h-10 items-center gap-2 rounded-md border border-slate-700 bg-slate-950 px-3 focus-within:border-indigo-500">
                    <LockOutlined className="text-slate-500" />
                    <input
                        autoComplete="current-password"
                        className="min-w-0 flex-1 bg-transparent text-sm text-slate-200 outline-none [&:-webkit-autofill]:[-webkit-box-shadow:0_0_0_1000px_#020617_inset] [&:-webkit-autofill]:[-webkit-text-fill-color:#e2e8f0]"
                        onChange={event => setCredentials(current => ({ ...current, password: event.target.value }))}
                        required
                        type="password"
                        value={credentials.password}
                    />
                </div>
            </label>
            {error && <div className="mb-4 rounded-md bg-rose-950/40 px-3 py-2 text-xs text-rose-400">{error}</div>}
            <button
                className="h-9 mt-3 rounded-md bg-indigo-600 text-xs font-bold text-white hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={isLoading}
                type="submit"
            >
                {isLoading ? "Signing in..." : "Sign in"}
            </button>
        </form>
    );
};

export default ApiLogin;
